"use client";

import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useRef } from "react";
import { cn } from "@/lib/utils";

type Variant = "primary" | "ghost" | "outline";

interface Props extends React.ComponentPropsWithoutRef<"a"> {
  variant?: Variant;
  size?: "md" | "lg";
}

export function MagneticButton({
  children,
  className,
  variant = "primary",
  size = "lg",
  ...rest
}: Props) {
  const ref = useRef<HTMLAnchorElement | null>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 200, damping: 18, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 200, damping: 18, mass: 0.4 });
  const tx = useTransform(sx, (v) => `${v}px`);
  const ty = useTransform(sy, (v) => `${v}px`);

  const handleMove = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const mx = e.clientX - (r.left + r.width / 2);
    const my = e.clientY - (r.top + r.height / 2);
    x.set(mx * 0.25);
    y.set(my * 0.35);
  };
  const reset = () => {
    x.set(0);
    y.set(0);
  };

  const base =
    "relative inline-flex items-center justify-center font-display font-bold tracking-wide uppercase rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";
  const sizes = {
    md: "px-6 py-3 text-xs",
    lg: "px-9 py-4 text-sm",
  };
  const variants: Record<Variant, string> = {
    primary:
      "bg-gold text-navy-deep shadow-[var(--shadow-glow)] hover:shadow-[0_25px_70px_-10px_color-mix(in_oklab,var(--gold)_60%,transparent)]",
    outline:
      "border border-navy/20 text-navy hover:bg-navy hover:text-primary-foreground",
    ghost: "text-navy hover:text-navy-deep",
  };

  return (
    <motion.a
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={reset}
      style={{ x: tx, y: ty }}
      className={cn(base, sizes[size], variants[variant], className)}
      {...(rest as object)}
    >
      <motion.span style={{ x: tx, y: ty }} className="relative z-10 flex items-center gap-2">
        {children}
      </motion.span>
    </motion.a>
  );
}
